// The one-time marker that tells the launch window (/lms/launch/:intent) a
// sign-in was started from THIS window for THIS launch (plan Task 20 fix
// rounds 1 and 2; Phase F Task 7 keys it by the launch's intent).
//
// Signing in is a round trip through the admin's own sign-in pages (SSO
// included). The marker is how the window knows the person at the keyboard
// just signed in for this launch rather than found a session already open in
// the tab: the window hands the mint the person's own token only after a
// sign-in the marker vouches for (a strict connection, and a learner whose
// domain requires its own sign-in, mint only then), and a session that was
// already open is never taken for one -- that is what keeps a shared device
// from launching as someone else's open session. Anyone whose browser keeps
// no marker signs in.
//
// One fixed sessionStorage key holds {intent, at}: the launch it is for and
// when it was left (ms since the epoch). Only the latest sign-in matters, so keys
// never pile up, and a marker counts for MAX_AGE only: an abandoned sign-in
// cannot link, hours later, whoever is signed in by then. sessionStorage
// because the sign-in tokens live there too: the marker is this tab's and
// goes with it. Every read and write is guarded; with storage unavailable
// the answer is "no marker" and the page asks.

const KEY = 'lms-launch-signin'

// How long a sign-in has to bring the learner back (SSO with a second factor
// can take a few minutes).
export const SIGNIN_MARK_MAX_AGE_MS = 15 * 60 * 1000

export const markSignIn = (intent, now = Date.now()) => {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify({ intent, at: now }))
  } catch {}
}

// The stored marker, or null when there is none or it is not one of ours.
const read = () => {
  try {
    const raw = window.sessionStorage.getItem(KEY)
    if (raw === null) return null
    const marker = JSON.parse(raw)
    return marker && typeof marker.intent === 'string' && Number.isFinite(marker.at) ? marker : { invalid: true }
  } catch {
    return null
  }
}

const counts = (marker, intent, now) => {
  if (!marker || marker.invalid) return false
  const age = now - marker.at
  return marker.intent === intent && age >= 0 && age <= SIGNIN_MARK_MAX_AGE_MS
}

// Whether a marker for this launch is there and fresh. Reads only.
export const hasSignInMark = (intent, now = Date.now()) => counts(read(), intent, now)

// Whether the marker for this launch was there and fresh. Whatever is stored
// under the key goes either way (expired, another launch's, unreadable). It
// fails closed: if it cannot be removed it cannot be trusted to be one-time,
// so the answer is no.
export const spendSignInMark = (intent, now = Date.now()) => {
  const marker = read()
  try {
    window.sessionStorage.removeItem(KEY)
  } catch {
    return false
  }
  return counts(marker, intent, now)
}

// Whether any launch's marker is there and fresh: this tab left for a sign-in
// that an LMS launch window started. The sign-in pages use it to keep that
// sign-in out of the remembered accounts (single sign-on returns included).
export const hasAnySignInMark = (now = Date.now()) => {
  const marker = read()
  return !!marker && !marker.invalid && now - marker.at >= 0 && now - marker.at <= SIGNIN_MARK_MAX_AGE_MS
}
